import type { ContestType } from '@betterpool/api-types/contests-service';

import type { SortDirection } from '~/domains/common/enums/sort-direction.enum';

import type { ContestStatusFilter } from '../domains/services/enums/contest-status-filter.enum';
import type { getLeaderboard, LeaderboardType } from '../domains/services/leaderboard.service.api';
import type { LeaderboardSortField } from '../enums/leaderboard-sort-field.enum';

const STALE_TIME = 30_000;
const INFINITE_SCROLL_PAGE_SIZE = 20;
const COMBINATIONS_PAGE_SIZE = 50;
export const CONTEST_QUERY_KEY_PREFIX = 'contest';

const ContestQueryKeys = {
  // TODO: Replace 'contests' with CONTEST_QUERY_KEY_PREFIX when the old gameshell will be deleted
  CONTEST_DETAIL: ({ contestId, userId }) => ['contests', contestId, userId],
  LEADERBOARD: ({
    contestId,
    entryId,
    includeRecord,
    leaderboardType,
    picksSlateId,
    segmentId,
    slateId,
    userId,
    search,
    followingOnly,
    sort,
    sortDirection,
  }: {
    contestId: string;
    enabled?: boolean;
    entryId?: string;
    includeRecord?: boolean;
    leaderboardType?: Parameters<typeof getLeaderboard>[2]['leaderboardType'];
    picksSlateId?: string;
    segmentId?: string;
    slateId: string;
    userId?: string;
    search?: string;
    followingOnly?: boolean;
    sort?: LeaderboardSortField;
    sortDirection?: SortDirection;
  }) =>
    [
      CONTEST_QUERY_KEY_PREFIX,
      contestId,
      slateId,
      'leaderboard',
      userId,
      entryId,
      leaderboardType,
      segmentId,
      includeRecord,
      picksSlateId,
      search,
      followingOnly,
      sort,
      sortDirection,
    ].filter(Boolean),
  LEADERBOARD_INFINITE: ({
    contestId,
    slateId,
    userId,
    leaderboardType,
    segmentId,
    includeRecord,
    picksSlateId,
    search,
    followingOnly,
    sort,
    sortDirection,
    pageSize,
  }: {
    contestId: string;
    slateId?: string;
    userId?: string;
    leaderboardType?: string;
    segmentId?: string;
    includeRecord?: boolean;
    picksSlateId?: string;
    search?: string;
    followingOnly?: boolean;
    sort?: LeaderboardSortField;
    sortDirection?: SortDirection;
    pageSize?: number;
  }) =>
    [
      CONTEST_QUERY_KEY_PREFIX,
      contestId,
      'leaderboardInfinite',
      userId,
      slateId,
      leaderboardType,
      segmentId,
      includeRecord,
      picksSlateId,
      search,
      followingOnly,
      sort,
      sortDirection,
      pageSize,
    ].filter(Boolean),
  LEADERBOARD_WITH_PICKS_INFINITE: ({
    contestId,
    slateId,
    userId,
    search,
    followingOnly,
    sort,
    sortDirection,
  }: {
    contestId: string;
    slateId?: string;
    userId?: string;
    search?: string;
    followingOnly?: boolean;
    sort?: LeaderboardSortField;
    sortDirection?: SortDirection;
  }) =>
    [
      CONTEST_QUERY_KEY_PREFIX,
      contestId,
      'leaderboardWithPicksInfinite',
      userId,
      slateId,
      search,
      followingOnly,
      sort,
      sortDirection,
    ].filter(Boolean),
  LEADERBOARD_PROP_WITH_PICKS_INFINITE: ({
    contestId,
    slateId,
    userId,
    search,
    followingOnly,
    sort,
    sortDirection,
  }: {
    contestId: string;
    slateId?: string;
    userId?: string;
    search?: string;
    followingOnly?: boolean;
    sort?: LeaderboardSortField;
    sortDirection?: SortDirection;
  }) =>
    [
      CONTEST_QUERY_KEY_PREFIX,
      contestId,
      'leaderboardPropWithPicksInfinite',
      userId,
      slateId,
      search,
      followingOnly,
      sort,
      sortDirection,
    ].filter(Boolean),
  LEADERBOARD_SUMMARY: ({
    contestId,
    slateId,
    segmentId,
    leaderboardType,
  }: {
    contestId: string;
    slateId: string;
    segmentId?: string;
    leaderboardType?: LeaderboardType;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'leaderboardSummary',
    segmentId,
    leaderboardType,
  ],
  USER_CONTESTS: ({
    contestType,
    hasEntryFee,
    leagues,
    limit,
    sort,
    status,
    userId,
  }: {
    contestType: ContestType;
    hasEntryFee?: boolean;
    leagues?: string[];
    limit: number;
    sort?: { option: string; dir: string };
    status: ContestStatusFilter;
    userId: string;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    'userContests',
    userId,
    status,
    leagues,
    contestType,
    limit,
    sort,
    hasEntryFee,
  ],
  PICKSHEET: ({
    contestId,
    slateId,
    entryId,
    sort,
  }: {
    contestId: string;
    slateId: string;
    entryId?: string;
    sort?: 'seed' | 'startTime';
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    entryId,
    'picks',
    'picksheet',
    ...(sort ? [sort] : []),
  ],
  PLAYERS_FOR_SLATE: ({
    contestId,
    slateId,
    sort,
    dir,
    position,
    onlyUserEntries,
  }: {
    contestId: string;
    slateId: string;
    sort?: 'score' | 'pick_count';
    dir?: 'asc' | 'desc';
    position?: { [key: string]: string | number };
    onlyUserEntries?: boolean;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'players',
    sort,
    dir,
    position,
    onlyUserEntries,
  ],
  ENTRIES_CURRENT_USER_WITH_PICKS: ({ contestId, userId }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    userId,
    'entriesWithPicks',
  ],
  ENTRY_BREAKDOWN_INFINITE: ({
    contestId,
    entryId,
    leaderboardType,
    segmentId,
    includeRecord,
  }: {
    contestId: string;
    entryId: string;
    leaderboardType?: 'CUMULATIVE' | 'SLATE' | 'SEGMENT';
    segmentId?: string;
    includeRecord?: boolean;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    entryId,
    'entryBreakdownInfinite',
    leaderboardType,
    segmentId,
    includeRecord,
  ],
  PICKS_AVAILABLE: ({
    contestId,
    teamIds = [],
  }: {
    contestId: string;
    teamIds?: readonly string[];
  }) => [CONTEST_QUERY_KEY_PREFIX, contestId, 'picksAvailable', teamIds],
  ENTRY_PICKS_AVAILABLE: ({ entryId }: { entryId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    entryId,
    'entryPicksAvailable',
  ],
  PICKS_UNAVAILABLE: ({
    contestId,
    teamIds = [],
  }: {
    contestId: string;
    teamIds?: readonly string[];
  }) => [CONTEST_QUERY_KEY_PREFIX, contestId, 'picksUnavailable', teamIds],
  ENTRY_PICKS_UNAVAILABLE: ({ entryId }: { entryId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    entryId,
    'entryPicksUnavailable',
  ],
  PICKS_CSV_UPLOADS: ({ contestId, slateId }: { contestId: string; slateId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'picksCsvUploads',
  ],
  AVAILABILITY_REPORT: ({
    contestId,
    limit,
    offset,
  }: {
    contestId: string;
    limit?: number;
    offset?: number;
  }) => [CONTEST_QUERY_KEY_PREFIX, contestId, 'availabilityReport', limit, offset],
  CONTEST_STATISTICS: (contestId: string) => [CONTEST_QUERY_KEY_PREFIX, contestId, 'statistics'],
  SLATE_COMBINATIONS: ({ contestId, slateId }: { contestId: string; slateId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'statistics',
    'combinations',
  ],
  COMBINATION_ENTRIES: ({
    contestId,
    slateId,
    comboKey,
  }: {
    contestId: string;
    slateId: string;
    comboKey: string;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'statistics',
    'combinations',
    comboKey,
    'entries',
  ],
  ENTRY_PICKS: ({
    contestId,
    slateId,
    entryId,
  }: {
    contestId: string;
    slateId: string;
    entryId: string;
  }) => [CONTEST_QUERY_KEY_PREFIX, contestId, slateId, entryId, 'picks'],
  ENTRY_PICKS_BULK: ({ entryId, slateIds }: { entryId: string; slateIds: string[] }) => [
    CONTEST_QUERY_KEY_PREFIX,
    entryId,
    'bulkPicks',
    ...slateIds,
  ],
  ENTRY_PICK_HISTORY: ({ entryId, segmentId }: { entryId: string; segmentId?: string | null }) => [
    CONTEST_QUERY_KEY_PREFIX,
    entryId,
    'pickHistory',
    segmentId ?? 'all',
  ],
  ENTRIES_FOR_CURRENT_USER: ({ contestId, userId }: { contestId: string; userId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    userId,
    'entriesForCurrentUser',
  ],
  CONTEST_LOCATION_ALLOWED_BY_TAGNAME: ({ tagName }: { tagName: ContestType }) => [
    CONTEST_QUERY_KEY_PREFIX,
    tagName,
    'locationAllowed',
  ],
  CONTEST_TEMPLATE_BY_LEAGUE_ID: ({ leagueId }: { leagueId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    leagueId,
    'contestTemplate',
  ],
  CONTEST_TEMPLATE_BY_CONTEST_TEMPLATE_ID: ({
    contestTemplateId,
  }: {
    contestTemplateId: string;
  }) => [CONTEST_QUERY_KEY_PREFIX, contestTemplateId, 'contestTemplateById'],
  CONTEST_LOCATION_SETTINGS: () => [CONTEST_QUERY_KEY_PREFIX, 'locationSettings'],
  CONTEST_INVITES_RECEIVED: ({ userId }: { userId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    'invites',
    'received',
    userId,
  ],
  GET_CONTESTS_RESTARTS: ({ userId }: { userId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    'restarts',
    userId,
  ],
  SHARED_COMMISSIONER_TEAM_PICKS: ({
    contestId,
    entryId,
    slateIds,
    shouldAllowCommissionerAccess,
    contestUser,
  }: {
    contestId: string;
    entryId: string;
    slateIds: string[];
    shouldAllowCommissionerAccess: boolean;
    contestUser?: string;
  }) => [
    CONTEST_QUERY_KEY_PREFIX,
    'sharedCommissionerPicks',
    'team',
    contestId,
    entryId,
    slateIds,
    shouldAllowCommissionerAccess,
    contestUser,
  ],
  SHARED_COMMISSIONER_PLAYER_PICKS: ({
    entryId,
    slateIds,
  }: {
    entryId: string;
    slateIds: string[];
  }) => [CONTEST_QUERY_KEY_PREFIX, 'sharedCommissionerPicks', 'player', entryId, slateIds],
  STANDINGS_REPORT: ({ contestId }: { contestId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    'standingsReport',
  ],
};

const ContestMutationKeys = {
  PICKS_SUBMIT: ({ contestId, slateId }: { contestId: string; slateId: string }) => [
    'contest',
    contestId,
    slateId,
    'picksSubmit',
  ],
  UPLOAD_PICKS_CSV: ({ contestId, slateId }: { contestId: string; slateId: string }) => [
    CONTEST_QUERY_KEY_PREFIX,
    contestId,
    slateId,
    'uploadPicksCsv',
  ],
  UPDATE_ENTRY: (entryId?: string) => ['contest', 'entry', entryId],
  UPDATE_CONTEST_INVITE: ({ userId }: { userId: string }) => ['contest', 'invites', userId],
  DISMISS_CONTEST_RESTART: () => [CONTEST_QUERY_KEY_PREFIX, 'restarts', 'dismiss'],
  DOWNLOAD_PICKS_CSV_TEMPLATE: () => [CONTEST_QUERY_KEY_PREFIX, 'downloadPicksCsvTemplate'],
};

export {
  COMBINATIONS_PAGE_SIZE,
  ContestMutationKeys,
  ContestQueryKeys,
  INFINITE_SCROLL_PAGE_SIZE,
  STALE_TIME,
};
